import React from "react";
import { Link } from "react-router-dom";

/**
 * SALES-ROLE-001A · Route-level error boundary.
 *
 * Wraps <Routes> so an unexpected render exception inside any
 * authenticated surface (including new Sales flows) surfaces a
 * clear, actionable fallback instead of a completely blank screen.
 * The boundary preserves the previously-signed-in session — the
 * caller can navigate back to the public landing manually.
 *
 * We intentionally DO NOT swallow errors elsewhere: this is the
 * outermost safety net, not a substitute for handling loading /
 * error / empty states inside individual pages.
 */
export default class RouteErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { error: null };
    }

    static getDerivedStateFromError(error) {
        return { error };
    }

    componentDidCatch(error, info) {
        // Surface in the browser console so preview / support can
        // grab the stack. Production still shows the fallback UI.
        // eslint-disable-next-line no-console
        console.error("[RouteErrorBoundary]", error, info?.componentStack);
    }

    handleReset = () => {
        this.setState({ error: null });
    };

    render() {
        if (!this.state.error) return this.props.children;
        return (
            <div
                data-testid="route-error-fallback"
                className="min-h-screen flex items-center justify-center bg-[#F5F5F7] px-6"
            >
                <div className="max-w-md w-full rounded-xl border border-[#E5E7EB] bg-white p-6 shadow-sm">
                    <div className="text-[10px] uppercase tracking-[0.16em] text-[#B45309] font-medium">
                        Unexpected error
                    </div>
                    <h1 className="mt-1 text-lg font-semibold text-[#131B2F]">
                        This page couldn't be displayed
                    </h1>
                    <p className="mt-2 text-sm text-[#4B5565]">
                        A problem stopped the page from rendering. Your session is still
                        signed in. Try returning to the home page and reopening the section
                        you were viewing.
                    </p>
                    <div className="mt-4 flex items-center gap-2">
                        <Link
                            to="/"
                            data-testid="route-error-home"
                            onClick={this.handleReset}
                            className="inline-flex items-center h-9 px-3 rounded-md bg-[#131B2F] text-white text-sm hover:bg-[#0F1626]"
                        >
                            Go home
                        </Link>
                        <button
                            type="button"
                            data-testid="route-error-retry"
                            onClick={this.handleReset}
                            className="inline-flex items-center h-9 px-3 rounded-md border border-[#E5E7EB] bg-white text-sm text-[#131B2F] hover:bg-[#F5F5F7]"
                        >
                            Try again
                        </button>
                    </div>
                </div>
            </div>
        );
    }
}
